import React, { Component } from 'react';
import moment from 'moment';
import * as ScrollMagic from 'scrollmagic';

import config from '@config';
import {
  checkDateHasProgram,
  scrollGridToTarget,
  scrollGridForDuration,
  scrollTimelinetoTarget,
} from '@tvguide/helpers';

class Timeline extends Component {
  constructor(props) {
    super(props);
    this.smController;
    this.debouncedTimeout;
    this.generatedTimeline;
    this.scenes;

    this.state = {
      lastRequestedDate: null,
      isScrollDisabled: false,
      updatedTimeline: 0,
    };
  }

  componentDidMount() {
    window.addEventListener(
      config.events.renderedGrid,
      this.onGridRendered.bind(this),
    );
    this.generatedTimeline = this.generateTimeline(
      this.props.beginDate,
      this.props.endDate,
    );
  }

  componentDidUpdate(prevProps) {
    if (
      (this.props.pixelRatioChanged && !prevProps.pixelRatioChanged) ||
      this.props.minutesPixelRatio !== prevProps.minutesPixelRatio
    ) {
      const startTime = moment(this.props.displayedTime.start);
      const endTime = moment(this.props.displayedTime.end);
      const diff = Math.floor(endTime.diff(startTime, 'hours') / 2);
      startTime.add(diff, 'hours');

      this.generatedTimeline = this.generateTimeline(
        this.props.beginDate,
        this.props.endDate,
      );
      this.setState({
        updatedTimeline: this.state.updatedTimeline + 1,
      });

      setTimeout(
        (() => {
          scrollGridToTarget(
            document.querySelector(
              `[data-time="${startTime.format('YYYY-MM-DD HH:mm:ss')}"]`,
            ),
            null,
            this.props.hasRendered.bind(this),
          );
        }).bind(this),
        10,
      );
    }
  }

  componentWillUnmount() {
    window.removeEventListener(config.events.renderedGrid, this.onGridRendered);
    if (this.smController) {
      this.smController = this.smController.destroy(true);
      this.smController = null;
      this.scenes.forEach(scene => {
        scene.destroy();
        scene = null;
      });
      this.scenes = null;
    }
  }

  onGridRendered() {
    this.initScrollMagic();
    this.initDoubleScroll();
  }

  initDoubleScroll() {
    // Taken from: https://stackoverflow.com/questions/9236314/how-do-i-synchronize-the-scroll-position-of-two-divs
    var isSyncingLeftScroll = false;
    var isSyncingRightScroll = false;
    var leftDiv = document.getElementById('timeline-container');
    var rightDiv = document.getElementById('sm-container');

    if (leftDiv) {
      leftDiv.onscroll = function() {
        if (!isSyncingLeftScroll) {
          isSyncingRightScroll = true;
          rightDiv.scrollLeft = this.scrollLeft;
        }
        isSyncingLeftScroll = false;
      };
    }
    if (rightDiv) {
      rightDiv.onscroll = function() {
        if (!isSyncingRightScroll) {
          isSyncingLeftScroll = true;
          leftDiv.scrollLeft = this.scrollLeft;
        }
        isSyncingRightScroll = false;
      };
    }
  }

  initScrollMagic() {
    const dataTime = document.querySelectorAll('[data-time]');
    const timeline = document.getElementById('timeline-container');

    if (this.smController) {
      return;
    }

    if (dataTime.length && timeline) {
      this.smController = new ScrollMagic.Controller({
        container: '#timeline-container',
        vertical: false,
        refreshInterval: 0,
      });

      this.scenes = [];

      dataTime.forEach(el => {
        const scene = new ScrollMagic.Scene({
          triggerElement: el,
          triggerHook: 0,
          offset: 0, // 30 * this.props.minutesPixelRatio,
        });

        scene.on('start', this.debounceScroll.bind(this));
        scene.addTo(this.smController);

        this.scenes.push(scene);
      });
    }
  }

  generateDayTimeline(day) {
    const elements = [];

    for (let i = 0; i < 24; i++) {
      let text;
      switch (true) {
        case i >= 13:
          text = `${i - 12}pm`;
          break;
        case i === 0:
          text = '12am';
          break;
        case i === 12:
          text = '12pm';
          break;
        default:
          text = `${i}am`;
          break;
      }

      const liProps = {
        key: `${day}-${i}`,
        style: { width: `${60 * this.props.minutesPixelRatio}px` },
        'data-time': `${day} ${i >= 10 ? i : `0${i}`}:00:00`,
      };

      elements.push(React.createElement('li', liProps, text));
    }

    return elements;
  }

  generateTimeline(fromDay, toDay) {
    const listElements = [];
    const now = moment();
    const daysBetween = toDay.diff(fromDay, 'days');

    for (let i = 0; i <= daysBetween; i++) {
      listElements.push(
        this.generateDayTimeline(
          fromDay
            .clone()
            .add(i, 'days')
            .format('YYYY-MM-DD'),
        ),
      );
    }

    return listElements;
  }

  debounceScroll(e) {
    clearTimeout(this.debouncedTimeout);
    this.debouncedTimeout = setTimeout(this.handleScroll.bind(this, e), 500);
  }

  handleScroll(e) {
    if (this.props.pixelRatioChanged) {
      return;
    }

    const trigger = e.target.triggerElement();

    const smContainer = document.getElementById('timeline-container');
    const nbElements = Math.ceil(
      (smContainer.offsetWidth +
        (smContainer.getBoundingClientRect().left -
          trigger.getBoundingClientRect().left * -1)) /
        (60 * this.props.minutesPixelRatio),
    );

    let endTarget = trigger;
    let middleTarget = trigger;
    for (let i = 0; i < nbElements; i++) {
      endTarget = endTarget.nextElementSibling
        ? endTarget.nextElementSibling
        : endTarget;

      if (i < nbElements / 2 - 1) {
        middleTarget = middleTarget.nextElementSibling;
      }
    }

    const middleDate = moment(middleTarget.dataset.time);
    const formattedMiddleDate = middleDate.format('YYYY-MM-DD');

    if (this.props.activeDay !== formattedMiddleDate) {
      this.props.setActiveDay(formattedMiddleDate);
    }

    if (
      this.props.displayedTime.start !== trigger.dataset.time &&
      this.props.displayedTime.end !== endTarget.dataset.time
    ) {
      this.props.setDisplayedTime(trigger.dataset.time, endTarget.dataset.time);
      const smContainer = document.getElementById('sm-container');
      smContainer.scrollLeft = smContainer.scrollLeft - 1;
    }

    if (
      formattedMiddleDate !== this.state.lastRequestedDate &&
      !checkDateHasProgram(formattedMiddleDate)
    ) {
      this.setState({
        lastRequestedDate: formattedMiddleDate,
      });

      this.props.requestTvguide(
        this.props.nid,
        moment.utc(formattedMiddleDate).unix(),
      );
    }
  }

  /**
   * handle pagination cta click and move grid for 1h
   * @param  {Number} direction Positive for forward, negative for backward
   */
  handlePaginationClick(direction) {
    scrollGridForDuration(direction * 60);
  }

  render() {
    return (
      <div className="tvg-grid__hours__container">
        <div
          className="tvg-grid__pagination-cta tvg-grid__pagination-cta--left"
          onClick={this.handlePaginationClick.bind(this, -1)}
        >
          <i className="icon-chevron" />
        </div>
        <div
          className="tvg-grid__pagination-cta tvg-grid__pagination-cta--right"
          onClick={this.handlePaginationClick.bind(this, 1)}
        >
          <i className="icon-chevron" />
        </div>
        <div className="tvg-grid__hours__scroll" id="timeline-container">
          <ul
            className="tvg-grid__hours"
            style={{
              width: `${this.props.gridWidth}px`,
            }}
          >
            {this.generatedTimeline}
          </ul>
        </div>
      </div>
    );
  }
}

export default Timeline;
